import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, can, getUser } from '../lib/api';
import { date, dateTime, number } from '../lib/format';
import { Card, Empty, ErrorNote, Field, Loading, Modal, PageHead, Pill, useResource, useToast } from '../components/ui';
import { Attachments } from '../components/Attachments';
import { RowActions } from '../components/RowActions';

/**
 * Data processors: the systems that share this organisation's personal data.
 * Each is registered once, holds its own key, asks this platform before it
 * uses a person's data, and is told when a decision changes.
 */

type Scope = 'consent:check' | 'consent:capture' | 'consent:read' | 'suppression:read';

interface Processor {
  id: string; name: string; kind: string; contact: string; description: string; status: 'active' | 'disabled'; createdAt: string;
  key: { id: string; prefix: string; scopes: Scope[]; status: string; lastUsedAt: string | null; createdAt: string } | null;
  previousKey: { prefix: string; expiresAt: string } | null;
  rotation: { everyDays: number | null; overlapDays: number; dueAt: string | null; overdue: boolean };
  notify: { id: string; url: string; events: string[]; status: string; lastDeliveredAt: string | null; delivered7d: number; failed7d: number } | null;
  usage30d: { checks: number; allowed: number; denied: number; captures: number; reads: number; withdrawals: number; suppressionPulls: number };
}

const KIND: Record<string, string> = {
  crm: 'CRM', marketing: 'Marketing platform', messaging: 'Messaging gateway', analytics: 'Analytics',
  erp: 'ERP', access: 'Access control', support: 'Support desk', custom: 'Other',
};
const SCOPE: Array<[Scope, string, string]> = [
  ['consent:check', 'Check consent', 'Ask whether a purpose is granted for a person before using their data.'],
  ['consent:capture', 'Record consent', 'Submit decisions taken in its own screens.'],
  ['consent:read', 'Read consent state', 'Full state and history for one person.'],
  ['suppression:read', 'Pull withdrawals', 'The list of people who withdrew, for systems that cannot receive calls.'],
];

export default function Processors() {
  const toast = useToast();
  const writable = can(getUser(), 'processors:edit');
  const [reload, setReload] = useState(0);
  const [adding, setAdding] = useState(false);
  const [issued, setIssued] = useState<{ name: string; key: string; secret: string | null } | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const list = useResource<{ processors: Processor[] }>(() => api.get('/v1/processors'), [reload]);
  const usage = useResource<{ byDay: Array<{ day: string; allowed: number; denied: number; captures: number }> }>(
    () => api.get(`/v1/processors/usage${selected ? `?processorId=${selected}` : ''}`), [reload, selected],
  );

  const rows = list.data?.processors ?? [];
  const checks = rows.reduce((n, p) => n + p.usage30d.checks, 0);
  const allowed = rows.reduce((n, p) => n + p.usage30d.allowed, 0);
  const captures = rows.reduce((n, p) => n + p.usage30d.captures, 0);
  const told = rows.reduce((n, p) => n + (p.notify?.delivered7d ?? 0), 0);

  async function act(path: string, body: unknown, done: string) {
    try {
      await api.post(path, body);
      toast(done, 'ok');
      setReload((n) => n + 1);
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  async function rotate(p: Processor) {
    const keeps = p.rotation.overlapDays > 0 ? `The current key keeps working for ${p.rotation.overlapDays} days.` : 'The current key stops working at once.';
    if (!window.confirm(`Issue a new key for ${p.name}? ${keeps}`)) return;
    try {
      const res = await api.post<{ key: string }>(`/v1/processors/${p.id}/rotate-key`);
      setIssued({ name: p.name, key: res.key, secret: null });
      setReload((n) => n + 1);
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  return (
    <>
      <PageHead title="Data processors">
        {writable && <button className="primary" onClick={() => setAdding(true)}>Register processor</button>}
      </PageHead>

      <p className="page-line">
        <strong>{number(checks)}</strong> consent checks in 30 days · {checks ? `${Math.round((allowed / checks) * 1000) / 10}% found consent` : 'none yet'}{captures ? ` · ${number(captures)} recorded by processors` : ''}
      </p>

      <ErrorNote error={list.error} />
      {list.loading && !list.data && <Loading what="Loading processors" />}

      {list.data && (rows.length === 0 ? <Card><Empty>No processors.</Empty></Card> : (
        <Card>
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Processor</th>
                  <th>May</th>
                  <th className="num">Checks · 30 days</th>
                  <th className="num">Consent present</th>
                  <th>Told of withdrawals</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className={selected === p.id ? 'row-on' : ''} onClick={() => setSelected(selected === p.id ? null : p.id)} style={{ cursor: 'pointer' }}>
                    <td><strong>{p.name}</strong><div className="small muted">{KIND[p.kind] ?? p.kind}</div></td>
                    <td className="small">{(p.key?.scopes ?? []).map((s) => SCOPE.find(([c]) => c === s)?.[1] ?? s).join(', ')}</td>
                    <td className="num">{number(p.usage30d.checks)}</td>
                    <td className="num">{p.usage30d.checks ? `${Math.round((p.usage30d.allowed / p.usage30d.checks) * 1000) / 10}%` : ''}</td>
                    <td className="small">
                      {p.notify
                        ? <>{p.notify.delivered7d} delivered{p.notify.failed7d ? <>, <span className="bad-text">{p.notify.failed7d} failed</span></> : ''}</>
                        : <span className="muted">{p.key?.scopes.includes('suppression:read') ? 'Pulls withdrawals' : 'Not notified'}</span>}
                      {p.rotation.overdue && <div><Pill tone="bad">Key rotation overdue</Pill></div>}
                    </td>
                    <td>{p.status === 'active' ? <span className="muted small">Active</span> : <Pill>Disabled</Pill>}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                      {writable && (
                        <RowActions more={[
                          ...(p.status === 'active' ? [{ label: `Rotate key${p.key ? ` (${p.key.prefix}…)` : ''}`, onClick: () => rotate(p) }] : []),
                          { label: p.status === 'active' ? 'Disable' : 'Enable', danger: p.status === 'active', onClick: () => act(`/v1/processors/${p.id}/status`, { status: p.status === 'active' ? 'disabled' : 'active' }, p.status === 'active' ? 'Disabled.' : 'Enabled.') },
                        ]} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      {selected && rows.find((p) => p.id === selected) && (
        <ProcessorDetails p={rows.find((p) => p.id === selected)!} writable={writable} onChanged={() => setReload((n) => n + 1)} />
      )}

      <Card title={`Consent checks by day${selected ? ` · ${rows.find((p) => p.id === selected)?.name ?? ''}` : ''}`}>
        {usage.data && usage.data.byDay.some((d) => d.allowed || d.denied || d.captures)
          ? <UsageChart rows={usage.data.byDay} />
          : <Empty>No calls in the last 30 days.</Empty>}
      </Card>

      {adding && (
        <RegisterModal
          onClose={() => setAdding(false)}
          onDone={(name, key, secret) => { setAdding(false); setIssued({ name, key, secret }); setReload((n) => n + 1); }}
        />
      )}

      {issued && (
        <Modal
          title={`Key for ${issued.name}`}
          description="Shown once."
          onClose={() => setIssued(null)}
          footer={<button className="primary" onClick={() => setIssued(null)}>Done</button>}
        >
          <pre className="mono keyblock">{issued.key}</pre>
          <button className="small" onClick={() => navigator.clipboard.writeText(issued.key).then(() => toast('Copied.', 'ok'))}>Copy key</button>
          {issued.secret && (
            <>
              <h2 className="section">Signing secret for notifications</h2>
              <pre className="mono keyblock">{issued.secret}</pre>
              <button className="small" onClick={() => navigator.clipboard.writeText(issued.secret!).then(() => toast('Copied.', 'ok'))}>Copy secret</button>
              <div className="small muted" style={{ marginTop: 6 }}>Header X-Wekalp-Signature: t=&lt;unix&gt;,v1=HMAC-SHA256(secret, "&lt;t&gt;.&lt;body&gt;").</div>
            </>
          )}
          <h2 className="section">Checking consent</h2>
          <pre className="mono keyblock">{`GET ${window.location.origin}/v1/consents/<email or phone>/<purpose key>/check?identifierType=email
X-Service-Key: ${issued.key.slice(0, 14)}…

{ "live": true, "action": "granted", "since": "…", "expiresAt": "…" }`}</pre>
        </Modal>
      )}
    </>
  );
}

function RegisterModal({ onClose, onDone }: { onClose: () => void; onDone: (name: string, key: string, secret: string | null) => void }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [kind, setKind] = useState('crm');
  const [contact, setContact] = useState('');
  const [scopes, setScopes] = useState<Scope[]>(['consent:check']);
  const [notify, setNotify] = useState(false);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);

  async function create() {
    setBusy(true);
    try {
      const res = await api.post<{ key: string; signingSecret: string | null }>('/v1/processors', {
        name, kind, contact, scopes, ...(notify && url ? { notify: { url, events: ['withdrawn', 'expired'] } } : {}),
      });
      onDone(name, res.key, res.signingSecret);
    } catch (err) {
      toast((err as Error).message, 'bad');
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Register processor"
      onClose={onClose}
      footer={<><button onClick={onClose} disabled={busy}>Cancel</button><button className="primary" onClick={create} disabled={busy || !name.trim() || !scopes.length || (notify && !url)}>{busy ? 'Registering…' : 'Register'}</button></>}
    >
      <div className="row">
        <Field label="Name"><input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Membership CRM" /></Field>
        <Field label="Kind">
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            {Object.entries(KIND).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Contact" help="Who runs the system."><input type="text" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="it@example.com" /></Field>
      <Field label="May">
        {SCOPE.map(([code, label, help]) => (
          <label key={code} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '5px 0', fontWeight: 400 }}>
            <input type="checkbox" style={{ width: 'auto', marginTop: 3 }} checked={scopes.includes(code)} onChange={(e) => setScopes((s) => (e.target.checked ? [...s, code] : s.filter((x) => x !== code)))} />
            <span>{label}<div className="small muted">{help}</div></span>
          </label>
        ))}
      </Field>
      <label className="fb-check">
        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
        Tell this system when consent is withdrawn or expires
      </label>
      {notify && (
        <Field label="Endpoint" help="HTTPS. Signed with a secret shown once."><input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://crm.example.com/hooks/consent" /></Field>
      )}
    </Modal>
  );
}

function UsageChart({ rows }: { rows: Array<{ day: string; allowed: number; denied: number; captures: number }> }) {
  const max = Math.max(1, ...rows.map((r) => r.allowed + r.denied));
  const width = 900; const height = 170; const pad = 26;
  const step = (width - pad * 2) / Math.max(1, rows.length);
  const y = (v: number) => height - pad - (v / max) * (height - pad * 2);
  return (
    <div>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img" aria-label="Consent checks by day, split by answer">
        <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} stroke="var(--line)" />
        <text x={pad} y={pad - 8} fontSize="10" fill="var(--ink-3)">{max}</text>
        {rows.map((r, i) => {
          const x = pad + i * step + step * 0.15; const w = step * 0.7;
          return (
            <g key={r.day}>
              <rect x={x} y={y(r.allowed + r.denied)} width={w} height={y(r.allowed) - y(r.allowed + r.denied)} fill="var(--warn)" />
              <rect x={x} y={y(r.allowed)} width={w} height={height - pad - y(r.allowed)} fill="var(--ok)" />
            </g>
          );
        })}
        {rows.map((r, i) => (i % Math.ceil(rows.length / 8) === 0 ? <text key={r.day} x={pad + i * step} y={height - 8} fontSize="9" fill="var(--ink-3)">{r.day.slice(5)}</text> : null))}
      </svg>
      <div className="small muted" style={{ display: 'flex', gap: 14 }}>
        <span><span className="swatch" style={{ background: 'var(--ok)' }} /> Consent present</span>
        <span><span className="swatch" style={{ background: 'var(--warn)' }} /> Not present</span>
      </div>
    </div>
  );
}

/** Rotation policy and evidence files for the selected processor. */
function ProcessorDetails({ p, writable, onChanged }: { p: Processor; writable: boolean; onChanged: () => void }) {
  const toast = useToast();
  const [every, setEvery] = useState<string>(p.rotation.everyDays ? String(p.rotation.everyDays) : '');
  const [overlap, setOverlap] = useState<string>(String(p.rotation.overlapDays));
  const [busy, setBusy] = useState(false);
  const dirty = every !== (p.rotation.everyDays ? String(p.rotation.everyDays) : '') || overlap !== String(p.rotation.overlapDays);

  async function save() {
    setBusy(true);
    try {
      await api.put(`/v1/processors/${p.id}/rotation`, { everyDays: every ? Number(every) : null, overlapDays: Number(overlap) });
      toast('Rotation policy saved.', 'ok');
      onChanged();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid cols-2">
      <Card title={`Key rotation · ${p.name}`}>
        <div className="row">
          <Field label="Rotate every">
            <select value={every} disabled={!writable} onChange={(e) => setEvery(e.target.value)}>
              <option value="">No schedule</option>
              <option value="30">30 days</option>
              <option value="90">90 days</option>
              <option value="180">180 days</option>
              <option value="365">365 days</option>
            </select>
          </Field>
          <Field label="Old key keeps working for">
            <select value={overlap} disabled={!writable} onChange={(e) => setOverlap(e.target.value)}>
              <option value="0">No overlap</option>
              <option value="7">7 days</option>
              <option value="14">14 days</option>
              <option value="30">30 days</option>
            </select>
          </Field>
        </div>
        <dl className="kv">
          <dt>Current key issued</dt><dd>{p.key ? date(p.key.createdAt) : '—'}</dd>
          <dt>Rotation due</dt><dd>{p.rotation.dueAt ? <>{date(p.rotation.dueAt)}{p.rotation.overdue && <> <Pill tone="bad">Overdue</Pill></>}</> : 'No schedule'}</dd>
          {p.previousKey && <><dt>Previous key</dt><dd><span className="mono">{p.previousKey.prefix}…</span> works until {date(p.previousKey.expiresAt)}</dd></>}
        </dl>
        {writable && <button className="small primary" onClick={save} disabled={busy || !dirty} style={{ marginTop: 10 }}>{busy ? 'Saving…' : 'Save policy'}</button>}
      </Card>
      <Card title="Contract and evidence files">
        <Attachments entityType="processor" entityId={p.id} canAdd={writable} />
      </Card>
    </div>
  );
}
